import React, { useState } from 'react';
import {
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  Box,
} from '@mui/material';
import {
  History as HistoryIcon,
  SaveAlt as StoreIcon,
  Restore as RestoreIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { api } from '../services/api';

const RESTORE_FILTER_NOTE = 'The snapshot is adapted to the device\'s current radios and interfaces — not a byte-for-byte rollback.';

const formatStoredAt = (storedAt) => {
  if (!storedAt) return '';
  const d = new Date(storedAt);
  if (Number.isNaN(d.getTime())) return String(storedAt);
  return d.toLocaleString();
};

/**
 * scope: 'device' (single serial) | 'all' (project devices)
 */
const LastKnownGoodMenu = ({
  scope = 'device',
  serial = '',
  onComplete,
  disabled = false,
  buttonSx,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [menuStatus, setMenuStatus] = useState(null);
  const [confirmRestoreOpen, setConfirmRestoreOpen] = useState(false);
  const [confirmStoreOpen, setConfirmStoreOpen] = useState(false);
  const [storeConfirmText, setStoreConfirmText] = useState('');
  const [restoreConfirmText, setRestoreConfirmText] = useState('');
  const [viewOpen, setViewOpen] = useState(false);
  const [viewJson, setViewJson] = useState('');
  const [viewStoredAt, setViewStoredAt] = useState('');
  const [viewLoading, setViewLoading] = useState(false);

  const menuOpen = Boolean(anchorEl);
  const isAll = scope === 'all';

  const closeMenu = () => setAnchorEl(null);

  const emit = (payload) => {
    if (typeof onComplete === 'function') {
      onComplete(payload);
    }
  };

  const openMenu = async (e) => {
    setAnchorEl(e.currentTarget);
    if (!isAll && serial) {
      try {
        const status = await api.getLastKnownGoodStatus(serial);
        setMenuStatus(status);
      } catch {
        setMenuStatus(null);
      }
    } else {
      setMenuStatus(null);
    }
  };

  const performStore = async () => {
    setConfirmStoreOpen(false);
    setLoading(true);
    try {
      const resp = isAll
        ? await api.storeAllLastKnownGood()
        : await api.storeLastKnownGood(serial);
      const replaced = resp?.replaced_existing
        || (Array.isArray(resp?.results) && resp.results.some((r) => r.replaced_existing));
      emit({
        type: 'success',
        action: 'store',
        scope,
        serial,
        response: resp,
        message: isAll
          ? `Stored last known good for ${resp?.succeeded ?? 0} jnet2 device(s)${replaced ? ' (some snapshots were replaced)' : ''}`
          : (resp?.message || 'Last known good configuration stored'),
      });
    } catch (err) {
      emit({
        type: 'error',
        action: 'store',
        scope,
        serial,
        message: err.message || 'Failed to store last known good configuration',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStoreClick = async () => {
    closeMenu();
    setLoading(true);
    let openedStoreConfirm = false;
    try {
      if (isAll) {
        const status = await api.getAllLastKnownGoodStatus();
        const count = status?.with_snapshot ?? 0;
        if (count > 0) {
          setStoreConfirmText(
            `${count} jnet2 device(s) already have a last known good snapshot. `
            + 'Storing now will overwrite those snapshots with the current configuration. Continue?'
          );
          setConfirmStoreOpen(true);
          openedStoreConfirm = true;
          return;
        }
      } else {
        const status = menuStatus || await api.getLastKnownGoodStatus(serial);
        if (status?.available) {
          const when = formatStoredAt(status.stored_at);
          setStoreConfirmText(
            when
              ? `This device already has a last known good snapshot from ${when}. Storing now will overwrite it with the current configuration. Continue?`
              : 'This device already has a last known good snapshot. Storing now will overwrite it with the current configuration. Continue?'
          );
          setConfirmStoreOpen(true);
          openedStoreConfirm = true;
          return;
        }
      }
      await performStore();
    } catch (err) {
      emit({
        type: 'error',
        action: 'store',
        scope,
        serial,
        message: err.message || 'Failed to check last known good status',
      });
    } finally {
      if (!openedStoreConfirm) {
        setLoading(false);
      }
    }
  };

  const performRestore = async () => {
    setConfirmRestoreOpen(false);
    setLoading(true);
    try {
      const resp = isAll
        ? await api.restoreAllLastKnownGood()
        : await api.restoreLastKnownGood(serial);
      const offline = resp?.device_offline || resp?.result?.device_offline;
      const bulkOffline = Array.isArray(resp?.results)
        && resp.results.some((r) => r.device_offline);
      emit({
        type: 'success',
        action: 'restore',
        scope,
        serial,
        response: resp,
        message: isAll
          ? `Restored ${resp?.succeeded ?? 0} device(s) from last known good (${resp?.skipped ?? 0} skipped, ${resp?.failed ?? 0} failed)`
          : (resp?.message || (offline
            ? 'Configuration restored and saved. Device is offline and will apply when connected.'
            : 'Configuration restored from last known good')),
        deviceOffline: offline || bulkOffline,
      });
    } catch (err) {
      const noSnapshot = err?.data?.result?.no_snapshot || err?.data?.no_snapshot;
      emit({
        type: 'error',
        action: 'restore',
        scope,
        serial,
        message: noSnapshot
          ? (isAll ? 'No last known good snapshot available for this device' : `No last known good configuration stored for ${serial}`)
          : (err.message || 'Failed to restore last known good configuration'),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreClick = async () => {
    closeMenu();
    setLoading(true);
    let openedRestoreConfirm = false;
    try {
      if (isAll) {
        const status = await api.getAllLastKnownGoodStatus();
        const count = status?.with_snapshot ?? 0;
        if (count === 0) {
          emit({
            type: 'error',
            action: 'restore',
            scope,
            serial,
            message: 'No jnet2 devices have a stored last known good snapshot',
          });
          return;
        }
        setRestoreConfirmText(
          `Restore last known good for ${count} device(s)? This overwrites each device's active configuration and pushes to connected devices. ${RESTORE_FILTER_NOTE}`
        );
        setConfirmRestoreOpen(true);
        openedRestoreConfirm = true;
        return;
      }

      const status = menuStatus || await api.getLastKnownGoodStatus(serial);
      if (!status?.available) {
        emit({
          type: 'error',
          action: 'restore',
          scope,
          serial,
          message: `No last known good configuration stored for ${serial}`,
        });
        return;
      }
      const when = formatStoredAt(status.stored_at);
      setRestoreConfirmText(
        when
          ? `Restore the snapshot stored on ${when}? This overwrites the current configuration for ${serial} and pushes when online. ${RESTORE_FILTER_NOTE}`
          : `Restore the stored last known good configuration for ${serial}? This overwrites the current configuration and pushes when online. ${RESTORE_FILTER_NOTE}`
      );
      setConfirmRestoreOpen(true);
      openedRestoreConfirm = true;
    } catch (err) {
      emit({
        type: 'error',
        action: 'restore',
        scope,
        serial,
        message: err.message || 'Failed to check last known good status',
      });
    } finally {
      if (!openedRestoreConfirm) {
        setLoading(false);
      }
    }
  };

  const handleViewClick = async () => {
    closeMenu();
    setViewOpen(true);
    setViewLoading(true);
    setViewJson('');
    setViewStoredAt('');
    try {
      const status = await api.getLastKnownGoodStatus(serial, { includeConfig: true });
      if (!status?.available || !status.configuration) {
        emit({
          type: 'error',
          action: 'view',
          scope,
          serial,
          message: `No last known good configuration stored for ${serial}`,
        });
        setViewOpen(false);
        return;
      }
      setViewStoredAt(formatStoredAt(status.stored_at));
      setViewJson(JSON.stringify(status.configuration, null, 2));
    } catch (err) {
      emit({
        type: 'error',
        action: 'view',
        scope,
        serial,
        message: err.message || 'Failed to load last known good configuration',
      });
      setViewOpen(false);
    } finally {
      setViewLoading(false);
    }
  };

  const canView = !isAll && menuStatus?.available;

  return (
    <>
      <Tooltip title={isAll ? 'Last known good — jnet2 CAP and Node devices' : 'Last known good configuration (jnet2)'}>
        <span>
          <IconButton
            size="small"
            disabled={disabled || loading || (!isAll && !serial)}
            onClick={openMenu}
            sx={buttonSx}
          >
            {loading ? (
              <CircularProgress size={16} />
            ) : (
              <HistoryIcon sx={{ fontSize: '0.875rem' }} />
            )}
          </IconButton>
        </span>
      </Tooltip>

      <Menu anchorEl={anchorEl} open={menuOpen} onClose={closeMenu}>
        {canView && (
          <Tooltip title="View the stored last known good configuration JSON" placement="left">
            <MenuItem onClick={handleViewClick} disabled={loading}>
              <ListItemIcon><ViewIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="View last known good" />
            </MenuItem>
          </Tooltip>
        )}
        <Tooltip title="Save the current device configuration JSON as the last known good snapshot" placement="left">
          <MenuItem onClick={handleStoreClick} disabled={loading}>
            <ListItemIcon><StoreIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Store last known good" />
          </MenuItem>
        </Tooltip>
        <Tooltip
          title={isAll
            ? `Restore every jnet2 device that has a stored snapshot. ${RESTORE_FILTER_NOTE}`
            : `Restore this device from its stored snapshot. ${RESTORE_FILTER_NOTE}`}
          placement="left"
        >
          <MenuItem onClick={handleRestoreClick} disabled={loading}>
            <ListItemIcon><RestoreIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Restore last known good" />
          </MenuItem>
        </Tooltip>
      </Menu>

      <Dialog open={confirmStoreOpen} onClose={() => { setConfirmStoreOpen(false); setLoading(false); }}>
        <DialogTitle>Overwrite existing snapshot?</DialogTitle>
        <DialogContent>
          <DialogContentText>{storeConfirmText}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setConfirmStoreOpen(false); setLoading(false); }}>Cancel</Button>
          <Button onClick={performStore} color="warning" variant="contained" autoFocus>
            Store anyway
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmRestoreOpen} onClose={() => { setConfirmRestoreOpen(false); setLoading(false); }}>
        <DialogTitle>Restore last known good?</DialogTitle>
        <DialogContent>
          <DialogContentText>{restoreConfirmText}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setConfirmRestoreOpen(false); setLoading(false); }}>Cancel</Button>
          <Button onClick={performRestore} color="warning" variant="contained" autoFocus>
            Restore
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={viewOpen} onClose={() => setViewOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          Last known good — {serial}
          {viewStoredAt ? ` (stored ${viewStoredAt})` : ''}
        </DialogTitle>
        <DialogContent dividers>
          {viewLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TextField
              fullWidth
              multiline
              minRows={16}
              maxRows={28}
              value={viewJson}
              InputProps={{ readOnly: true }}
              sx={{ fontFamily: 'monospace', '& textarea': { fontFamily: 'monospace', fontSize: '0.8rem' } }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LastKnownGoodMenu;
